import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {ShadowHighlightContextAware} from "../../../common/elements";
import Styles from "./feedSpotlight.lit.scss";
import {EopImageSpinner} from "../../../page/elements/spinner";
import {DefaultFeedSource} from "../feedSource";
import {schedule} from "../../../common/utils/promises";
import {FeedEntry} from "../feedEntry";
import {VisitRecord} from "../contentsearch/visitRecord";
import {resolve} from "../../../container";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP, type BootstrapBreakpoint} from "../../../common/resolutionConstants";
import {Dictionary} from "../../../page/elements/dictionary";
import {handleActivationKey} from "../../../common/utils/events";
import {TrackingEventType} from "../../../tracking/types";
import {ELEMENT_SCROLL_PADDING, ScrollService} from "../../../common/scroll";
import {ANIMATION_DURATION} from "../../../common/gridLayout";
import {Timeout} from "../../../common/timeout";
import {ResizeObserverFactory} from "../../../common/observation";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {ifDefined} from "lit/directives/if-defined.js";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {classMap} from "lit/directives/class-map.js";
import {trackingAccordionInteract, trackingTeaserInteract} from "../../../tracking/trackingAttributes";

export const CLOSED_STANDARD_TILE_HEIGHT = 137;
export const PIXEL_ERROR_MARGIN = 2;
const NUMBER_OF_TILES = 3;


@customElement("eop-feed-spotlight")
export class EopFeedSpotlight extends ShadowHighlightContextAware(LitElement) {

    public static readonly styles = Styles;

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "sources"})
    public sources: string;
    @property({attribute: "keywords"})
    public keywords: string;
    @property({attribute: "excluded-keywords"})
    public excludedKeywords: string;
    @property({attribute: "character-limit", type: Number})
    public characterLimit: number = 180;
    @query("eop-image-spinner")
    protected spinner: EopImageSpinner;
    @queryAll(".feed-tile")
    private items: NodeListOf<HTMLElement>;
    @query(".spotlight-container")
    private container: HTMLElement;
    @state()
    private feedEntries: FeedEntry[] = [];
    @state()
    private breakpoint: Nullable<BootstrapBreakpoint> = null;
    @state()
    private currentIndex: number = 0;

    private dictionary: Dictionary;
    private resizeObserver: ResizeObserver;

    public constructor(
        private timeout: Timeout = resolve(Timeout),
        private resolution: Resolution = resolve(Resolution),
        private visitRecord: VisitRecord = resolve(VisitRecord),
        private scrollService: ScrollService = resolve(ScrollService),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private feedSource: DefaultFeedSource = new DefaultFeedSource(),
        private dates: LocatedDates = resolve(LocatedDates)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.configureFeedSource();
        this.dictionary = Dictionary.of(this);
        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateHeights());
        this.resolution.onBootstrapBreakpointChange(breakpoint => {
            this.breakpoint = breakpoint;
        });
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        void schedule(this.spinner.spinWhile(this.fetchFeedEntries())
            .then(() => {
                this.updateHeights();
                this.resizeObserver.observe(this.container);
            })).as("fetch");
    }

    private updateHeights(): void {
        this.items.forEach(item => this.setCalculatedHeight(item));
    }

    private setCalculatedHeight(item: HTMLElement): void {
        const descriptionHeight = item.querySelector<HTMLElement>(".description")!.outerHeight({includeMargin: true});
        const linkHeight = (item.querySelector<HTMLElement>(".link"))!.getBoundingClientRect().height;
        const feedImageHeight = item.querySelector<HTMLElement>(".feed-image")?.getBoundingClientRect().height ?? 0;
        item.style.setProperty("--collapsible-height", `${descriptionHeight + linkHeight + PIXEL_ERROR_MARGIN}px`);
        item.style.setProperty("--collapsible-image-height", `${feedImageHeight}px`);
    }

    private configureFeedSource(): void {
        this.feedSource.configure({
            sources: this.sources?.parseAsJSON() ?? {},
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language
        });
    }

    private async fetchFeedEntries(): Promise<boolean> {
        const feeds = await this.feedSource.fetch(NUMBER_OF_TILES, 0, this.visitRecord.getLastSessionDate());
        this.feedEntries = feeds
            .map((feed) => FeedEntry.fromResponse(feed)
                .inChannelContext(this.feedSource.channels())
                .beingRecentlyVisited(this.visitRecord.isVisited(feed.url)));
        return this.updateComplete;
    }

    public render(): TemplateResult {
        return html`
            <div class="spotlight-container">
                <eop-image-spinner></eop-image-spinner>
                ${this.renderTiles()}
            </div>`;
    }

    private renderTiles(): TemplateResult {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            return this.renderMobile();
        } else {
            return this.renderDesktop();
        }
    }

    private renderMobile(): TemplateResult {
        return html`
            <div class="feed-tiles">
                ${this.renderFeedTile(true)}
            </div>
        `;
    }

    private renderDesktop(): TemplateResult {
        return html`
            ${this.renderFeedTileImages()}
            <div class="feed-tiles">
                ${this.renderFeedTile(false)}
            </div>
        `;
    }

    private renderFeedTile(withImage: boolean): Array<TemplateResult> {
        return this.feedEntries.map((entry, index) => {
                const classes = {"reverse-background": index === this.currentIndex};
                return html`
                    <div class="feed-tile ${classMap(classes)}"
                         ?open=${index === this.currentIndex}
                         data-id=${index}
                         tabindex=${ifDefined(index === this.currentIndex ? undefined : "0")}
                         @click=${this.handleFeedTileClick}
                         @keydown=${this.handleFeedTileKeydown}
                    >
                        ${withImage ? this.renderFeedFeedImageContainer(index) : ""}
                        <div class="content">
                            <div class="header" ${trackingAccordionInteract(entry.data.title ?? `accordion item ${index}`, TrackingEventType.OPEN)}>
                                <div class="metadata">
                                    ${this.renderNewLabel(entry)}
                                    <div class="meta-info">${this.renderMetaInfo(entry)}</div>
                                    <span class=toggle-chevron></span>
                                </div>
                                <div class=headline>${unsafeHTML(entry.data.headline)}</div>
                            </div>
                            <div class="tile-footer">
                                ${this.renderDescription(entry)}
                                ${this.renderArticleLink(entry, index)}
                            </div>
                        </div>
                    </div>
                    ${this.renderSeparator(index)}
                `;
            }
        );
    }

    private renderFeedFeedImageContainer(index: number): TemplateResult {
        return html`
            <div class="image">
                ${this.renderFeedTileImage(index)}
            </div>`;
    }

    private renderFeedTileImages(): Array<TemplateResult> {
        return this.feedEntries.map((_, index) => this.renderFeedTileImage(index));
    }

    private renderFeedTileImage(index: number): TemplateResult {
        const entry = this.feedEntries[index];
        return html`
            <div class="feed-image" ?open=${(index === this.currentIndex)}>
                <eop-responsive-image
                        .imageSrc=${this.getImageUrl(entry)}
                        .imageAlt=${entry?.data.imageAltText}
                ></eop-responsive-image>
            </div>`;
    }

    private getImageUrl(entry: Maybe<FeedEntry>): string {
        if (!entry) {
            return "";
        }
        const breakpoint = this.breakpoint || this.resolution.getBootstrapBreakpoint();
        if (breakpoint === BOOTSTRAP.XS) {
            return entry.getImageUrlFor7x4Purpose();
        } else if (breakpoint === BOOTSTRAP.SM || breakpoint === BOOTSTRAP.MD) {
            return entry.getImageUrlFor3x4Purpose();
        } else {
            return entry.getImageUrlFor4x3Purpose();
        }
    }

    private renderNewLabel(entry: FeedEntry): Nullable<TemplateResult> {
        if (!this.isNotVisited(entry)) {
            return null;
        }
        return html`<span class="label-new">${this.dictionary.translate("NEW_TAG")}</span>`;
    }

    private isNotVisited(entry: FeedEntry): boolean {
        return entry.data.isNew && entry.recentlyVisited === false;
    }

    private renderDescription(entry: FeedEntry): TemplateResult {
        return html`
            <div class="description"><p>${entry.data.subHeadline ? unsafeHTML(entry.data.subHeadline) : ""}</p></div>`;
    }

    private renderArticleLink(entry: FeedEntry, index: number): TemplateResult {
        const openArticleLabel = this.dictionary.translate("OPEN_ARTICLE");
        return html`
            <div class="article-link">
                <eop-page-overlay-link
                        href=${entry.data.url}
                        title=${entry.data.title}
                        class="link"
                        data-id=${index}
                        @click=${() => this.handleFeedTileOpenClick(entry)}
                        ${trackingTeaserInteract(entry.data.title || openArticleLabel, entry.data.url)}
                >
                    ${openArticleLabel}
                </eop-page-overlay-link>
            </div>
        `;
    }

    private renderSeparator(index: number): Nullable<TemplateResult> {
        if (index < NUMBER_OF_TILES - 1) {
            return html`
                <div class="separator-line"></div>`;
        }
        return null;
    }


    private handleFeedTileClick(event: Event): void {
        const index = Number.parseInt((event.currentTarget as HTMLElement).dataset.id ?? "0");
        if (this.currentIndex === index) {
            return;
        }
        this.scrollToFeedItem(index);
        this.currentIndex = index;
    }

    private handleFeedTileKeydown(event: KeyboardEvent): void {
        handleActivationKey(event, () => this.handleFeedTileClick(event));
    }

    private scrollToFeedItem(index: number): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            const itemToOpen = this.items[index];
            const activeItem = this.items[this.currentIndex];
            const collapsibleHeight = activeItem.style.getPropertyValue("--collapsible-height").toInt();
            const collapsibleImageHeight = activeItem.style.getPropertyValue("--collapsible-image-height").toInt();
            let scrollMargin: number;
            if (index === 0) {
                scrollMargin = ELEMENT_SCROLL_PADDING;
            } else if (index < this.currentIndex) {
                scrollMargin = CLOSED_STANDARD_TILE_HEIGHT;
            } else {
                scrollMargin = CLOSED_STANDARD_TILE_HEIGHT + collapsibleHeight + collapsibleImageHeight;
            }
            void this.timeout.delay(() => {
                void this.scrollService.scrollToElement(itemToOpen, scrollMargin, ANIMATION_DURATION);
            });
        }
    }

    private handleFeedTileOpenClick(entry: FeedEntry): void {
        if (this.isNotVisited(entry)) {
            this.visitRecord.addVisitedURL(entry.data.url);
        }
    }

    private renderMetaInfo(entry: FeedEntry): string {
        const date = this.dates.toLocalDateString(new Date(entry.data.publishedDate));
        if (entry.data.newsType) {
            return `${date} | ${entry.data.newsType}`;
        }
        return date;
    }
}